import { FC, PropsWithChildren, useEffect } from "react";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import useOpenId from "./hooks/useOpenId";
import { LoadingAnimation } from "../LoadingAnimation";
import { createSearchParams, useNavigate, useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/hooks";

interface AuthProtectionProvider extends PropsWithChildren {
  path?: string;
  protection: "private" | "public" | "unprotected";
}

// This provider checks the url and the current cookies for authentication
export const AuthProtectionProvider: FC<AuthProtectionProvider> = ({
  path="",
  protection,
  children
}) => {
  
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const Settings = useSettingsContext();  
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirectOpenId } = useOpenId();

  const isAuthenticated = useAppSelector(store => store.auth.isAuthenticated);
  const isAuthenticating = useAppSelector(store => store.auth.isAuthenticating);
  const loginType = useAppSelector(store => store.auth.type);
  const loginStatus = useAppSelector(store => store.auth.status);
  const loginStatusCode = useAppSelector(store => store.auth.statusCode);

  const handleRedirectLogIn = () => {
    const {
      redirectPath, pathParams, ...restSearchParams
    } = Object.fromEntries(searchParams);

    if (redirectPath) {
      navigate({
        pathname: t(redirectPath, pathParams ? JSON.parse(pathParams) : {}) as string,
        search: createSearchParams(restSearchParams).toString()
      });
    } else {
      navigate(t(`common:url.${Settings.homepage ?? "invoices"}`));
    }
  }

  const handleRedirectLogOut = () => {
    navigate({
      pathname: t("common:url.login"),
      search: createSearchParams({
        ...(path && {redirectPath: path}),
        ...(!!Object.entries(params).length && {pathParams: JSON.stringify(params)}),
        ...(!!searchParams.toString() && Object.fromEntries(searchParams))
      }).toString()
    });
  }

  const handleRedirectError = (code: number) => {
    navigate({
      pathname: t("common:url.error"),
      search: createSearchParams({
        code: code.toString()
      }).toString()
    });
  }

  const handleLoadingAnimation = (children: React.ReactNode) => {

    if (isAuthenticating) {

      if ( 
        // Quan s'inicia sessió per credencials cal ensenyar el loader
        // de la vista de credencials i no el loader global
        path == "common:url.login" && loginType == "USER_AND_PASSWORD"
      ) {
        return children;
      }

    } else {

      if (isAuthenticated) {

        if (
          (protection == "private" && 
            // Wait for user settings to load
            Settings.target == "user") ||
          protection == "unprotected"
        ) {
          return children;
        }

      }

      if (!isAuthenticated) {
        if (
          protection == "public" || 
          protection == "unprotected"
        ) {
          return children;
        }
        
      }

    }

    return <LoadingAnimation />
  }

  useEffect(() => {

    if (!isAuthenticating) {

      if (isAuthenticated) {

        if (
          protection == "public" &&

          // Login handles redirects by itself
          !(loginType == "USER_AND_PASSWORD" && path == "common:url.login")
        ) {
          handleRedirectLogIn();
        }

      }
      
      if (!isAuthenticated) {
        if (
          // LoginNotMade
          (loginType == "none" || loginType == "logout") &&
          protection == "private"
        ) {

          if ( Settings.features?.oidc && loginType != "logout") {

            if (
              !( 
                (path == "common:url.connect" && searchParams.size == 0) ||
                (Settings.oidc?.routes.allowDefaultLogin && 
                path == "common:url.login")
              ) 
            ) {
              redirectOpenId(path, searchParams);
              return;
            }

          } else {

            handleRedirectLogOut();
          }

        }

        if ( 
          loginType != "none" &&
          loginStatus == "error" && 
          !(loginType == "USER_AND_PASSWORD") 
        ) {
          handleRedirectError(loginStatusCode ?? 401);
        }

      }
    }   

  }, [isAuthenticating, isAuthenticated, loginType, loginStatus, protection]);

  return handleLoadingAnimation(children);
}
